// frontend/src/pages/WeeklyCalendarPage.js

import React, { useEffect, useMemo, useState } from "react";
import { Star, CaretLeft, CaretRight } from "phosphor-react";
import BackButton from "../components/BackButton";
import ActivityLightbox from "../components/ActivityLightbox";
import { api } from "../api";

/* ---------- helpers ---------- */
const pad2 = (n) => String(n).padStart(2, "0");

const toYMD = (dt) =>
  `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;

const dateKey = (val) => {
  if (!val) return null;

  if (typeof val === "string") {
    const m = val.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }

  const d = new Date(val);
  if (Number.isNaN(d)) return null;

  return toYMD(d);
};

const groupByDay = (list = []) => {
  const map = {};

  for (const a of list) {
    const key = dateKey(a.timestamp ?? a.created_at);
    if (!key) continue;
    (map[key] ||= []).push(a);
  }

  for (const arr of Object.values(map)) {
    arr.sort(
      (a, b) =>
        new Date(a.timestamp ?? a.created_at) -
        new Date(b.timestamp ?? b.created_at)
    );
  }

  return map;
};

export default function WeeklyCalendarPage() {
  const [activitiesByDay, setActivitiesByDay] = useState({});
  const [lb, setLb] = useState({ open: false, activity: null, id: null });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () =>
      setIsMobile(window.matchMedia("(max-width: 767px)").matches);

    check();
    window.addEventListener("resize", check);

    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/feed/my-activities-by-day");
        if (res?.data && typeof res.data === "object") {
          setActivitiesByDay(res.data);
          return;
        }
      } catch {
        // fall through
      }

      try {
        const me = (await api.get("/users/me")).data;

        if (!me?.id) {
          setActivitiesByDay({});
          return;
        }

        const acts = (await api.get(`/users/${me.id}/activities`)).data || [];
        setActivitiesByDay(groupByDay(Array.isArray(acts) ? acts : []));
      } catch {
        setActivitiesByDay({});
      }
    })();
  }, []);

  const getMonday = (dateLike) => {
    const base = new Date(dateLike || new Date());
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate());
    const day = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - day);
    return d;
  };

  const [weekOffset, setWeekOffset] = useState(0);

  const anchorMonday = useMemo(() => getMonday(new Date()), []);

  const start = useMemo(() => {
    const d = new Date(anchorMonday);
    d.setDate(d.getDate() + weekOffset * 7);
    return d;
  }, [anchorMonday, weekOffset]);

  const days = useMemo(() => {
    const arr = [];

    for (let i = 0; i < 7; i += 1) {
      const dt = new Date(start);
      dt.setDate(start.getDate() + i);

      const ymd = toYMD(dt);

      const stars = (activitiesByDay[ymd] || []).slice().sort(
        (a, b) =>
          new Date(a.timestamp || a.created_at) -
          new Date(b.timestamp || b.created_at)
      );

      arr.push({
        label: dt.toLocaleString("en-US", { weekday: "short" }).toUpperCase(),
        dateLabel: `${dt.getMonth() + 1}/${dt.getDate()}`,
        dayNum: dt.getDate(),
        ymd,
        stars,
      });
    }

    return arr;
  }, [activitiesByDay, start]);

  const prevDisabled = weekOffset <= -1;
  const nextDisabled = weekOffset >= 0;

  return (
    <div className="min-h-screen pb-20 bg-blue-50">
      <header
        className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mt-2 mx-auto px-4 flex items-center">
          <BackButton className="text-blue-600" ariaLabel="Back" />

          <div className="absolute left-1/2 -translate-x-1/2 flex items-center justify-center">
            <button
              type="button"
              onClick={() =>
                !prevDisabled && setWeekOffset((o) => Math.max(-1, o - 1))
              }
              disabled={prevDisabled}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition disabled:cursor-not-allowed"
              aria-label="Previous week"
              title="Previous week"
            >
              <CaretLeft
                size={23}
                weight="fill"
                className={prevDisabled ? "text-blue-300" : "text-blue-400"}
              />
            </button>

            <h1 className="text-[20px] text-blue-800 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none whitespace-nowrap">
              {weekOffset === 0 ? "This Week" : "Last Week"}
            </h1>

            <button
              type="button"
              onClick={() =>
                !nextDisabled && setWeekOffset((o) => Math.min(0, o + 1))
              }
              disabled={nextDisabled}
              className="grid h-9 w-9 place-items-center rounded-full text-blue-400 transition disabled:cursor-not-allowed"
              aria-label="Next week"
              title="Next week"
            >
              <CaretRight
                size={23}
                weight="fill"
                className={nextDisabled ? "text-blue-300" : "text-blue-400"}
              />
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto px-4 mt-2">
        {/* Mobile: keep wide stacked day boxes */}
        <div className="md:hidden space-y-3">
          {days.map((d) => {
            const starSize = isMobile && d.stars.length > 1 ? 12 : 16;

            return (
              <div
                key={d.ymd}
                className="
                  rounded-3xl border border-blue-100/80 bg-white/90
                  px-4 py-3 min-h-[90px] flex flex-col justify-between
                  shadow-[0_4px_0px_rgba(37,99,235,0.96)]
                "
              >
                <div className="flex items-baseline gap-2 outfit-font">
                  <div className="text-blue-600 instrument-font text-sm font-bold uppercase">
                    {d.label}
                  </div>

                  <div className="text-blue-400 text-sm instrument-font font-semibold leading-none">
                    {d.dateLabel}
                  </div>
                </div>

                {d.stars?.length > 0 && (
                  <div className="-mt-1 flex gap-2 flex-wrap">
                    {d.stars.map((a) => (
                      <button
                        key={a.id}
                        title="View activity"
                        onClick={() =>
                          setLb({ open: true, activity: null, id: a.id })
                        }
                        className="p-0.5 rounded active:scale-95 transition"
                      >
                        <Star
                          size={starSize}
                          weight="fill"
                          className="text-blue-400 transition-colors cursor-pointer"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Desktop/tablet */}
        <div className="hidden md:grid md:grid-cols-7 md:gap-3 mt-3">
          {days.map((d) => {
            const starSize = d.stars.length > 4 ? 14 : 16;

            return (
              <div
                key={d.ymd}
                className="
                  relative min-h-[120px] overflow-hidden rounded-[22px]
                  border border-blue-100/80 bg-white/90 p-2
                  shadow-[0_10px_30px_rgba(37,99,235,0.06)]
                  transition hover:-translate-y-0.5
                  hover:shadow-[0_16px_40px_rgba(37,99,235,0.10)]
                "
              >
                <div className="text-blue-600 text-sm font-semibold">
                  {d.label}
                </div>

                <div className="text-blue-400 text-sm font-semibold leading-none">
                  {d.dateLabel}
                </div>

                {d.stars?.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 auto-rows-max gap-1 pr-1">
                    {d.stars.map((a) => (
                      <button
                        key={a.id}
                        title="View activity"
                        onClick={() =>
                          setLb({ open: true, activity: null, id: a.id })
                        }
                        className="p-0.5 rounded active:scale-95 transition"
                      >
                        <Star
                          size={starSize}
                          weight="fill"
                          className="text-blue-400 transition-colors cursor-pointer"
                        />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </main>

      <ActivityLightbox
        open={lb.open}
        onClose={() => setLb({ open: false, activity: null, id: null })}
        activity={null}
        activityId={lb.id}
        className="p-4"
      />

      <div style={{ height: "4rem" }} />
    </div>
  );
}